import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCommunes } from '../../api/communes';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { Search, ChevronDown, X, UserCheck } from 'lucide-react';
export function ImpersonationSelector() {
    const { impersonated, startImpersonation, stopImpersonation } = useImpersonation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const containerRef = useRef(null);
    const searchTimeout = useRef(null);
    // Debounce search
    useEffect(() => {
        if (searchTimeout.current)
            clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => setDebouncedSearch(search), 200);
    }, [search]);
    // Close on click outside
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open)
            document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    const { data, isLoading } = useQuery({
        queryKey: ['superadmin-communes', debouncedSearch],
        queryFn: () => getCommunes({ search: debouncedSearch || undefined, limit: 50 }),
        enabled: open,
        staleTime: 5 * 60 * 1000,
    });
    const handleSelect = (commune) => {
        startImpersonation(commune);
        setOpen(false);
        setSearch('');
        navigate('/commune');
    };
    const handleStop = () => {
        stopImpersonation();
        navigate('/super-admin');
    };
    return (_jsxs("div", { ref: containerRef, className: "relative", children: [_jsxs("button", { onClick: () => setOpen(!open), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${impersonated
                    ? 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`, children: [impersonated ? (_jsxs(_Fragment, { children: [_jsx(UserCheck, { size: 16, className: "text-amber-600" }), _jsx("span", { children: impersonated.name })] })) : (_jsx("span", { className: "text-gray-500", children: "Mode super admin" })), _jsx(ChevronDown, { size: 14, className: "text-gray-400" })] }), open && (_jsxs("div", { className: "absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50", children: [_jsx("div", { className: "p-3 border-b border-gray-100", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { size: 14, className: "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" }), _jsx("input", { value: search, onChange: (e) => setSearch(e.target.value), placeholder: "Rechercher une commune...", className: "w-full pl-9 pr-3 py-2 border rounded-lg text-sm", autoFocus: true })] }) }), impersonated && (_jsxs("button", { onClick: handleStop, className: "w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2", children: [_jsx(X, { size: 14 }), "Quitter le mode assistance"] })), _jsx("div", { className: "max-h-80 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "p-4 text-center text-xs text-gray-400", children: "Chargement..." })) : data?.data.length === 0 ? (_jsx("div", { className: "p-4 text-center text-xs text-gray-400", children: "Aucune commune trouv\u00E9e." })) : (data?.data.map((c) => (_jsxs("button", { onClick: () => handleSelect(c), className: `w-full px-4 py-2.5 text-left hover:bg-gray-50 border-b last:border-b-0 border-gray-50 ${impersonated?.id === c.id ? 'bg-amber-50' : ''}`, children: [_jsx("div", { className: "text-sm font-medium text-gray-800", children: c.name }), _jsxs("div", { className: "text-xs text-gray-400", children: [c.slug, c.postal_code ? ` • ${c.postal_code}` : ''] })] }, c.id)))) })] }))] }));
}
